import { DatePipe } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { finalize, of, switchMap } from 'rxjs';
import {
  ActionMenuComponent,
  ConfirmEditModalComponent,
  ErrorBlockComponent,
  FloatingActionFabComponent,
  HoverIdComponent,
  ListFilterShortcutDirective,
  PageActionModalComponent,
  PageShellComponent,
  PagedFeedComponent,
} from '@clinic-platform/ui-components';

import { SystemAdminContextService } from '../../../core/context/system-admin-context.service';
import { resolveHttpErrorMessageKey } from '../../../core/http/http-error-message.util';
import { TranslatePipe } from '../../../core/i18n/translate.pipe';
import { PageHeaderComponent } from '../../../shared/ui/page-header/page-header.component';
import { CompaniesService } from '../data-access/companies.service';
import {
  BootstrapCompanyRequest,
  CompanyItem,
  CompanyListSort,
  CompanyStatusFilter,
  UpdateCompanyRequest,
} from '../data-access/companies.models';

type CompaniesFilterState = {
  query: string;
  status: CompanyStatusFilter;
  sort: CompanyListSort;
};

@Component({
  selector: 'app-companies-page',
  standalone: true,
  imports: [
    DatePipe,
    ReactiveFormsModule,
    ActionMenuComponent,
    ConfirmEditModalComponent,
    ErrorBlockComponent,
    FloatingActionFabComponent,
    HoverIdComponent,
    ListFilterShortcutDirective,
    PageActionModalComponent,
    PageShellComponent,
    PagedFeedComponent,
    TranslatePipe,
    PageHeaderComponent,
  ],
  template: `
    <lib-page-shell
      libListFilterShortcut
      (libListFilterShortcutOpen)="openFiltersModal()"
    >
      <app-page-header
        title="Companies"
        subtitle="Tenant companies and initial company-admin accounts"
      />

      @if (successText) {
        <lib-error-block variant="success" [message]="successText" />
      }

      @if (actionErrorText) {
        <lib-error-block variant="error" [message]="(actionErrorText | t) ?? ''" />
      }

      <lib-paged-feed
        [items]="items"
        [collectionTemplate]="companiesTableTemplate"
        [loading]="isLoading"
        [loadingMore]="isLoadingMore"
        [hasMore]="hasMoreItems"
        [errorText]="errorText"
        emptyText="No tenant companies are available yet."
        loadMoreText="Load more companies"
        loadingMoreText="Loading more companies..."
        retryText="Retry"
        (loadMore)="loadMoreCompanies()"
        (retry)="refresh()"
      />

      <ng-template #companiesTableTemplate let-pageItems="items">
        <section class="companies-page__surface">
          <div class="companies-page__surface-head">
            <div>
              <h3>Company catalog</h3>
              <p>{{ items.length }} loaded</p>
            </div>
          </div>

          <div class="companies-page__table-wrap">
            <table class="companies-page__table">
              <thead>
                <tr>
                  <th>{{ 'Slug' | t }}</th>
                  <th>{{ 'Legal Name' | t }}</th>
                  <th>{{ 'Brand Name' | t }}</th>
                  <th>Admin</th>
                  <th>{{ 'Status' | t }}</th>
                  <th>{{ 'Created' | t }}</th>
                  <th>{{ 'Updated' | t }}</th>
                  <th>{{ 'Actions' | t }}</th>
                </tr>
              </thead>
              <tbody>
                @for (item of pageItems; track item.companyId) {
                  <tr class="companies-page__table-row">
                    <td>
                      <lib-hover-id [value]="item.companyId">
                        <span class="companies-page__entity-name">{{ item.slug }}</span>
                      </lib-hover-id>
                    </td>
                    <td>{{ item.legalName }}</td>
                    <td>{{ item.brandName ?? '—' }}</td>
                    <td>
                      <div>{{ item.companyAdminEmail ?? '—' }}</div>
                      @if (item.companyAdminPhone) {
                        <div class="companies-page__muted">{{ item.companyAdminPhone }}</div>
                      }
                    </td>
                    <td>
                      <span
                        class="companies-page__status-badge"
                        [class.companies-page__status-badge--disabled]="!item.isEnabled"
                      >
                        {{ item.isEnabled ? ('Enabled' | t) : ('Disabled' | t) }}
                      </span>
                    </td>
                    <td>{{ item.createdAt | date: 'dd.MM.yyyy HH:mm' }}</td>
                    <td>{{ item.updatedAt | date: 'dd.MM.yyyy HH:mm' }}</td>
                    <td class="companies-page__actions-cell">
                      <lib-action-menu [menuTemplate]="companyTableActionsMenu" buttonAriaLabel="Open company actions">
                        <ng-template #companyTableActionsMenu let-close="close">
                          <button
                            type="button"
                            class="action-menu__item"
                            (click)="editCompany(item); close()"
                          >
                            {{ 'Edit' | t }}
                          </button>
                          <button
                            type="button"
                            class="action-menu__item"
                            [class.action-menu__item--danger]="item.isEnabled"
                            [class.action-menu__item--primary]="!item.isEnabled"
                            (click)="toggleCompanyStatus(item); close()"
                          >
                            {{ item.isEnabled ? 'Disable' : 'Enable' }}
                          </button>
                          <button
                            type="button"
                            class="action-menu__item action-menu__item--danger"
                            (click)="deleteCompany(item); close()"
                          >
                            {{ 'Delete' | t }}
                          </button>
                        </ng-template>
                      </lib-action-menu>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>

          <div class="companies-page__cards">
            @for (item of pageItems; track item.companyId) {
              <article class="companies-page__card-item">
                <div class="companies-page__card-head">
                  <div>
                    <lib-hover-id [value]="item.companyId">
                      <div class="companies-page__card-title">{{ item.slug }}</div>
                    </lib-hover-id>
                    <div class="companies-page__card-subtitle">{{ item.legalName }}</div>
                  </div>
                  <span
                    class="companies-page__status-badge"
                    [class.companies-page__status-badge--disabled]="!item.isEnabled"
                  >
                    {{ item.isEnabled ? ('Enabled' | t) : ('Disabled' | t) }}
                  </span>
                </div>

                <div class="companies-page__card-grid">
                  <div class="companies-page__card-row">
                    <span>{{ 'Brand Name' | t }}</span>
                    <strong>{{ item.brandName ?? '—' }}</strong>
                  </div>
                  <div class="companies-page__card-row">
                    <span>Admin</span>
                    <strong>{{ item.companyAdminEmail ?? '—' }}</strong>
                  </div>
                  <div class="companies-page__card-row">
                    <span>{{ 'Created' | t }}</span>
                    <strong>{{ item.createdAt | date: 'dd.MM.yyyy HH:mm' }}</strong>
                  </div>
                  <div class="companies-page__card-row">
                    <span>{{ 'Updated' | t }}</span>
                    <strong>{{ item.updatedAt | date: 'dd.MM.yyyy HH:mm' }}</strong>
                  </div>
                </div>

                <div class="companies-page__card-actions">
                  <lib-action-menu [menuTemplate]="companyCardActionsMenu" buttonAriaLabel="Open company actions">
                    <ng-template #companyCardActionsMenu let-close="close">
                      <button
                        type="button"
                        class="action-menu__item"
                        (click)="editCompany(item); close()"
                      >
                        {{ 'Edit' | t }}
                      </button>
                      <button
                        type="button"
                        class="action-menu__item"
                        [class.action-menu__item--danger]="item.isEnabled"
                        [class.action-menu__item--primary]="!item.isEnabled"
                        (click)="toggleCompanyStatus(item); close()"
                      >
                        {{ item.isEnabled ? 'Disable' : 'Enable' }}
                      </button>
                      <button
                        type="button"
                        class="action-menu__item action-menu__item--danger"
                        (click)="deleteCompany(item); close()"
                      >
                        {{ 'Delete' | t }}
                      </button>
                    </ng-template>
                  </lib-action-menu>
                </div>
              </article>
            }
          </div>
        </section>
      </ng-template>

      <ng-template #pageActionsMenu let-close="close">
        <button
          type="button"
          role="menuitem"
          class="action-menu__item"
          (click)="openCreateModal(); close()"
        >
          Create company
        </button>
        <button
          type="button"
          role="menuitem"
          class="action-menu__item"
          (click)="openFiltersModal(); close()"
        >
          Filters & Sort (Ctrl+F)
        </button>
        @if (hasActiveFilters) {
          <button
            type="button"
            role="menuitem"
            class="action-menu__item"
            (click)="resetFilters(); close()"
          >
            Reset
          </button>
        }
        <button
          type="button"
          role="menuitem"
          class="action-menu__item"
          (click)="refresh(); close()"
        >
          Refresh
        </button>
      </ng-template>

      <lib-floating-action-fab
        [menuTemplate]="pageActionsMenu"
        buttonAriaLabel="Open company actions"
      />

      @if (isFiltersModalOpen) {
        <lib-page-action-modal
          [isOpen]="true"
          title="Filters & Sort"
          subtitle="Tune the company list"
          width="680px"
          (close)="closeFiltersModal()"
        >
          <form
            [formGroup]="filtersForm"
            class="companies-page__modal-grid"
            (submit)="$event.preventDefault(); applyFilters()"
          >
            <label>
              Search
              <input type="text" formControlName="query" placeholder="Slug, legal name, brand, admin, UUID" data-modal-primary-focus="true" />
            </label>
            <label>
              Status
              <select formControlName="status">
                <option value="">All</option>
                <option value="enabled">Enabled</option>
                <option value="disabled">Disabled</option>
              </select>
            </label>
            <label>
              Sort
              <select formControlName="sort">
                <option value="createdAtDesc">Newest created</option>
                <option value="createdAtAsc">Oldest created</option>
                <option value="updatedAtDesc">Newest updated</option>
                <option value="updatedAtAsc">Oldest updated</option>
                <option value="slugAsc">Slug A-Z</option>
                <option value="slugDesc">Slug Z-A</option>
                <option value="legalNameAsc">Legal name A-Z</option>
                <option value="legalNameDesc">Legal name Z-A</option>
                <option value="statusDesc">Enabled first</option>
                <option value="statusAsc">Disabled first</option>
              </select>
            </label>

            <div class="companies-page__modal-actions">
              <button type="button" class="companies-page__action" (click)="resetFilters()">Reset</button>
              <button type="submit" class="companies-page__submit">Apply Filters</button>
            </div>
          </form>
        </lib-page-action-modal>
      }

      @if (isCreateModalOpen) {
        <lib-page-action-modal
          [isOpen]="true"
          title="Create company"
          subtitle="Bootstrap a tenant and its initial company-admin account."
          width="720px"
          (close)="closeCreateModal()"
        >
          <form
            [formGroup]="form"
            (ngSubmit)="onSubmit()"
            class="companies-page__modal-grid"
          >
            <label>
              {{ 'Slug' | t }}
              <input type="text" formControlName="slug" data-modal-primary-focus="true" />
            </label>

            <label>
              {{ 'Legal Name' | t }}
              <input type="text" formControlName="legalName" />
            </label>

            <label>
              {{ 'Brand Name (optional)' | t }}
              <input type="text" formControlName="brandName" />
            </label>

            <label>
              {{ 'Company Admin Email' | t }}
              <input type="email" formControlName="companyAdminEmail" />
            </label>

            <div class="companies-page__modal-actions">
              <button
                type="button"
                class="companies-page__action"
                [disabled]="isSubmitting"
                (click)="closeCreateModal()"
              >
                Cancel
              </button>
              <button
                type="submit"
                class="companies-page__submit"
                [disabled]="form.invalid || isSubmitting"
              >
                {{ (isSubmitting ? 'Creating...' : 'Create company') | t }}
              </button>
            </div>
          </form>
        </lib-page-action-modal>
      }

      <lib-confirm-edit-modal
        [isOpen]="isEditModalOpen && !!editingCompany"
        title="Edit Company"
        metaPrimaryLabel="Company ID"
        [metaPrimaryValue]="editingCompany?.companyId ?? ''"
        metaSecondaryLabel="Created"
        [metaSecondaryValue]="editingCompany?.createdAt ? (editingCompany!.createdAt | date: 'dd.MM.yyyy HH:mm') ?? '' : ''"
        confirmLabel="Confirm current slug to unlock editing"
        confirmPlaceholder="Type current slug"
        [confirmValue]="editConfirmSlug"
        (confirmValueChange)="onEditConfirmSlugChange($event)"
        (cancel)="closeEditModal()"
      >
        @if (editingCompany) {
          <form [formGroup]="editForm" (ngSubmit)="saveEditModal()" class="companies-page__modal-grid">
            <label>
              Slug
              <input type="text" formControlName="slug" [disabled]="!isEditConfirmValid" data-modal-primary-focus="true" />
            </label>

            <label>
              Legal Name
              <input type="text" formControlName="legalName" [disabled]="!isEditConfirmValid" />
            </label>

            <label>
              Brand Name
              <input type="text" formControlName="brandName" [disabled]="!isEditConfirmValid" />
            </label>

            <label>
              Company Admin Email
              <input type="email" formControlName="companyAdminEmail" [disabled]="!isEditConfirmValid" />
            </label>

            <label>
              Company Admin Password
              <input type="password" formControlName="companyAdminPassword" [disabled]="!isEditConfirmValid" />
            </label>

            <label>
              Company Admin Phone
              <input type="tel" formControlName="companyAdminPhone" [disabled]="!isEditConfirmValid" />
            </label>

            <label class="companies-page__checkbox">
              <input type="checkbox" formControlName="isEnabled" [disabled]="!isEditConfirmValid" />
              Enabled
            </label>

            <div class="companies-page__modal-actions">
              <button type="button" class="companies-page__action" (click)="closeEditModal()">
                Cancel
              </button>
              <button
                type="submit"
                class="companies-page__submit"
                [disabled]="!isEditConfirmValid || editForm.invalid || isUpdatingCompany"
              >
                {{ isUpdatingCompany ? 'Saving...' : 'Save changes' }}
              </button>
            </div>
          </form>
        }
      </lib-confirm-edit-modal>
    </lib-page-shell>
  `,
  styles: [`
    .companies-page__surface {
      display: grid;
      gap: 14px;
      padding: 16px;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      background: rgba(255, 255, 255, 0.94);
    }

    .companies-page__surface-head {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 12px;
    }

    .companies-page__surface-head h3 {
      margin: 0;
      color: #0f172a;
      font-size: 18px;
    }

    .companies-page__surface-head p {
      margin: 4px 0 0;
      color: #475569;
      font-size: 13px;
    }

    .companies-page__table-wrap {
      overflow-x: auto;
    }

    .companies-page__table {
      width: 100%;
      min-width: 980px;
      border-collapse: collapse;
    }

    .companies-page__table th,
    .companies-page__table td {
      border-bottom: 1px solid #eef2f7;
      padding: 12px 10px;
      text-align: left;
      vertical-align: middle;
    }

    .companies-page__table th {
      background: rgba(248, 250, 252, 0.9);
      color: #0f172a;
      font-weight: 700;
    }

    .companies-page__table-row:hover {
      background: rgba(239, 246, 255, 0.6);
    }

    .companies-page__entity-name {
      color: #0f172a;
      font-weight: 700;
    }

    .companies-page__muted {
      margin-top: 3px;
      color: #64748b;
      font-size: 12px;
    }

    .companies-page__status-badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-height: 28px;
      padding: 0 10px;
      border-radius: 999px;
      background: #dcfce7;
      color: #166534;
      font-size: 12px;
      font-weight: 700;
    }

    .companies-page__status-badge--disabled {
      background: #e5e7eb;
      color: #374151;
    }

    .companies-page__actions-cell {
      width: 1%;
      white-space: nowrap;
    }

    .companies-page__cards {
      display: none;
      gap: 12px;
    }

    .companies-page__card-item {
      display: grid;
      gap: 12px;
      padding: 14px;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      background: #fff;
    }

    .companies-page__card-head {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 12px;
    }

    .companies-page__card-title {
      color: #0f172a;
      font-size: 16px;
      font-weight: 700;
    }

    .companies-page__card-subtitle {
      margin-top: 4px;
      color: #64748b;
      font-size: 13px;
    }

    .companies-page__card-grid {
      display: grid;
      gap: 8px;
    }

    .companies-page__card-row {
      display: grid;
      gap: 3px;
    }

    .companies-page__card-row span {
      color: #64748b;
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
    }

    .companies-page__card-row strong {
      color: #0f172a;
      font-size: 14px;
    }

    .companies-page__card-actions {
      display: flex;
      justify-content: flex-end;
    }

    .companies-page__modal-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
      gap: 12px;
      align-items: end;
    }

    .companies-page__modal-grid label {
      display: flex;
      flex-direction: column;
      gap: 6px;
      color: #374151;
      font-size: 13px;
      font-weight: 700;
    }

    .companies-page__modal-grid input[type='text'],
    .companies-page__modal-grid input[type='email'],
    .companies-page__modal-grid input[type='password'],
    .companies-page__modal-grid input[type='tel'],
    .companies-page__modal-grid select {
      min-height: 42px;
      box-sizing: border-box;
      border: 1px solid #cbd5e1;
      border-radius: 8px;
      padding: 0 12px;
      background: #fff;
      font: inherit;
    }

    .companies-page__checkbox {
      flex-direction: row !important;
      align-items: center;
      gap: 8px !important;
      min-height: 42px;
    }

    .companies-page__modal-actions {
      grid-column: 1 / -1;
      display: flex;
      justify-content: flex-end;
      gap: 8px;
      margin-top: 8px;
    }

    .companies-page__submit,
    .companies-page__action {
      min-height: 40px;
      border-radius: 8px;
      padding: 0 14px;
      border: 1px solid #d1d5db;
      background: #fff;
      color: #0f172a;
      font: inherit;
      font-weight: 700;
      cursor: pointer;
    }

    .companies-page__submit {
      border-color: #2563eb;
      background: #2563eb;
      color: #fff;
    }

    .companies-page__submit:disabled,
    .companies-page__action:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }

    @media (max-width: 900px) {
      .companies-page__table-wrap {
        display: none;
      }

      .companies-page__cards {
        display: grid;
      }
    }

    @media (max-width: 640px) {
      .companies-page__surface {
        padding: 12px;
      }

      .companies-page__card-head,
      .companies-page__modal-actions {
        flex-direction: column;
      }

      .companies-page__submit,
      .companies-page__action {
        width: 100%;
      }
    }
  `],
})
export class CompaniesPageComponent implements OnInit {
  private static readonly PAGE_LIMIT = 20;

  private readonly companiesService = inject(CompaniesService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly systemAdminContext = inject(SystemAdminContextService);

  protected readonly form = this.formBuilder.group({
    slug: this.formBuilder.nonNullable.control('', [
      Validators.required,
      Validators.pattern(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
    ]),
    legalName: this.formBuilder.nonNullable.control('', [Validators.required]),
    brandName: this.formBuilder.nonNullable.control(''),
    companyAdminEmail: this.formBuilder.nonNullable.control('', [
      Validators.required,
      Validators.email
    ]),
  });

  protected readonly editForm = this.formBuilder.group({
    slug: this.formBuilder.nonNullable.control('', [
      Validators.required,
      Validators.pattern(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
    ]),
    legalName: this.formBuilder.nonNullable.control('', [Validators.required]),
    brandName: this.formBuilder.nonNullable.control(''),
    companyAdminEmail: this.formBuilder.nonNullable.control('', [Validators.email]),
    companyAdminPassword: this.formBuilder.nonNullable.control('', [Validators.minLength(8)]),
    companyAdminPhone: this.formBuilder.nonNullable.control(''),
    isEnabled: this.formBuilder.nonNullable.control(true),
  });

  protected readonly filtersForm = this.formBuilder.group({
    query: this.formBuilder.nonNullable.control(''),
    status: this.formBuilder.nonNullable.control<CompanyStatusFilter>(''),
    sort: this.formBuilder.nonNullable.control<CompanyListSort>('createdAtDesc'),
  });

  protected items: CompanyItem[] = [];
  protected isLoading = true;
  protected isLoadingMore = false;
  protected hasMoreItems = false;
  protected errorText: string | null = null;
  protected isSubmitting = false;
  protected actionErrorText: string | null = null;
  protected successText: string | null = null;
  protected isCreateModalOpen = false;
  protected isFiltersModalOpen = false;
  protected isEditModalOpen = false;
  protected editingCompany: CompanyItem | null = null;
  protected editConfirmSlug = '';
  protected isUpdatingCompany = false;

  private nextCursor: string | null = null;
  private appliedFilters: CompaniesFilterState = this.getDefaultFilters();

  protected get hasActiveFilters(): boolean {
    const defaults = this.getDefaultFilters();
    return this.appliedFilters.query !== defaults.query
      || this.appliedFilters.status !== defaults.status
      || this.appliedFilters.sort !== defaults.sort;
  }

  protected get isEditConfirmValid(): boolean {
    const companySlug = this.editingCompany?.slug ?? '';
    return this.normalizeSlug(this.editConfirmSlug) === this.normalizeSlug(companySlug);
  }

  public ngOnInit(): void {
    this.refresh();
  }

  protected openCreateModal(): void {
    this.actionErrorText = null;
    this.form.reset({
      slug: '',
      legalName: '',
      brandName: '',
      companyAdminEmail: '',
    });
    this.form.markAsPristine();
    this.form.markAsUntouched();
    this.isCreateModalOpen = true;
  }

  protected closeCreateModal(): void {
    if (this.isSubmitting) {
      return;
    }

    this.isCreateModalOpen = false;
    this.actionErrorText = null;
  }

  protected onSubmit(): void {
    if (this.form.invalid || this.isSubmitting) {
      return;
    }

    const value = this.form.getRawValue();
    const payload: BootstrapCompanyRequest = {
      slug: this.normalizeSlug(value.slug),
      legalName: value.legalName.trim(),
      brandName: value.brandName.trim() ? value.brandName.trim() : undefined,
      companyAdminEmail: value.companyAdminEmail.trim().toLowerCase(),
    };

    this.isSubmitting = true;
    this.actionErrorText = null;
    this.successText = null;

    this.companiesService.bootstrap(payload).pipe(
      finalize(() => (this.isSubmitting = false)),
    ).subscribe({
      next: (created) => {
        this.successText = created.onboardingEmailQueued
          ? `Company created. Initial admin: ${created.companyAdminEmail}. Password setup email queued.`
          : `Company created. Initial admin: ${created.companyAdminEmail}. Onboarding email was not queued.`;
        this.isCreateModalOpen = false;
        this.refresh();
        this.systemAdminContext.triggerRefresh();
      },
      error: (error: unknown) => {
        this.actionErrorText = resolveHttpErrorMessageKey(error, {
          fallback: 'error.generic',
        });
      }
    });
  }

  protected openFiltersModal(): void {
    this.filtersForm.reset({ ...this.appliedFilters });
    this.isFiltersModalOpen = true;
  }

  protected closeFiltersModal(): void {
    this.isFiltersModalOpen = false;
  }

  protected applyFilters(): void {
    const value = this.filtersForm.getRawValue();
    this.appliedFilters = {
      query: value.query.trim(),
      status: value.status,
      sort: value.sort,
    };
    this.isFiltersModalOpen = false;
    this.refresh();
  }

  protected resetFilters(): void {
    const defaults = this.getDefaultFilters();
    this.appliedFilters = defaults;
    this.filtersForm.reset({ ...defaults });
    this.isFiltersModalOpen = false;
    this.refresh();
  }

  protected refresh(): void {
    this.nextCursor = null;
    this.hasMoreItems = false;
    this.loadPage(false);
  }

  protected loadMoreCompanies(): void {
    if (!this.hasMoreItems || this.isLoadingMore) {
      return;
    }

    this.loadPage(true);
  }

  protected toggleCompanyStatus(item: CompanyItem): void {
    const isEnabled = !item.isEnabled;
    const actionText = isEnabled ? 'enable' : 'disable';
    const confirmSlugRaw = window.prompt(
      `Type company slug to ${actionText}: ${item.slug}`,
      '',
    );

    if (confirmSlugRaw === null) {
      return;
    }

    const confirmSlug = this.normalizeSlug(confirmSlugRaw);
    if (!confirmSlug) {
      return;
    }

    this.actionErrorText = null;
    this.successText = null;

    this.companiesService.updateEnabled(item.companyId, isEnabled, confirmSlug).subscribe({
      next: () => {
        this.successText = `Company "${item.slug}" ${isEnabled ? 'enabled' : 'disabled'}.`;
        this.refresh();
        this.systemAdminContext.triggerRefresh();
      },
      error: (error: unknown) => {
        this.actionErrorText = resolveHttpErrorMessageKey(error, {
          fallback: 'error.generic',
        });
      }
    });
  }

  protected editCompany(item: CompanyItem): void {
    this.actionErrorText = null;
    this.successText = null;
    this.editingCompany = item;
    this.editConfirmSlug = '';
    this.isUpdatingCompany = false;
    this.editForm.patchValue({
      slug: item.slug,
      legalName: item.legalName,
      brandName: item.brandName ?? '',
      companyAdminEmail: item.companyAdminEmail ?? '',
      companyAdminPassword: '',
      companyAdminPhone: item.companyAdminPhone ?? '',
      isEnabled: item.isEnabled,
    });
    this.editForm.markAsPristine();
    this.editForm.markAsUntouched();
    this.isEditModalOpen = true;
  }

  protected deleteCompany(item: CompanyItem): void {
    const confirmSlugRaw = window.prompt(
      `Type company slug to delete permanently: ${item.slug}`,
      '',
    );

    if (confirmSlugRaw === null) {
      return;
    }

    const confirmSlug = this.normalizeSlug(confirmSlugRaw);
    if (!confirmSlug) {
      return;
    }

    this.actionErrorText = null;
    this.successText = null;

    this.companiesService.deleteCompany(item.companyId, confirmSlug).subscribe({
      next: () => {
        this.successText = `Company "${item.slug}" was deleted.`;
        this.refresh();
        this.systemAdminContext.triggerRefresh();
      },
      error: (error: unknown) => {
        this.actionErrorText = resolveHttpErrorMessageKey(error, {
          fallback: 'error.generic',
        });
      }
    });
  }

  protected closeEditModal(): void {
    this.isEditModalOpen = false;
    this.editingCompany = null;
    this.editConfirmSlug = '';
    this.isUpdatingCompany = false;
  }

  protected onEditConfirmSlugChange(value: string): void {
    this.editConfirmSlug = value;
  }

  protected saveEditModal(): void {
    if (!this.editingCompany || !this.isEditConfirmValid || this.editForm.invalid || this.isUpdatingCompany) {
      return;
    }

    const value = this.editForm.getRawValue();
    const payload: UpdateCompanyRequest = {
      slug: this.normalizeSlug(value.slug),
      legalName: value.legalName.trim(),
      brandName: value.brandName.trim() ? value.brandName.trim() : undefined,
      companyAdminEmail: value.companyAdminEmail.trim()
        ? value.companyAdminEmail.trim().toLowerCase()
        : undefined,
      companyAdminPassword: value.companyAdminPassword.trim()
        ? value.companyAdminPassword.trim()
        : undefined,
      companyAdminPhone: value.companyAdminPhone.trim(),
    };

    if (!payload.slug || !payload.legalName) {
      return;
    }

    this.isUpdatingCompany = true;
    this.actionErrorText = null;
    this.successText = null;

    this.companiesService.updateCompany(this.editingCompany.companyId, payload).pipe(
      switchMap((updatedCompany) => {
        if (updatedCompany.isEnabled === value.isEnabled) {
          return of(updatedCompany);
        }

        return this.companiesService.updateEnabled(
          this.editingCompany!.companyId,
          value.isEnabled,
          updatedCompany.slug,
        );
      }),
      finalize(() => (this.isUpdatingCompany = false)),
    ).subscribe({
      next: () => {
        this.successText = `Company "${this.editingCompany?.slug ?? ''}" updated.`;
        this.closeEditModal();
        this.refresh();
        this.systemAdminContext.triggerRefresh();
      },
      error: (error: unknown) => {
        this.actionErrorText = resolveHttpErrorMessageKey(error, {
          fallback: 'error.generic',
        });
      }
    });
  }

  private loadPage(append: boolean): void {
    if (append) {
      this.isLoadingMore = true;
    } else {
      this.isLoading = true;
      this.errorText = null;
    }

    this.companiesService.getPage({
      limit: CompaniesPageComponent.PAGE_LIMIT,
      cursor: append ? this.nextCursor : null,
      query: this.appliedFilters.query,
      status: this.appliedFilters.status,
      sort: this.appliedFilters.sort,
    }).pipe(
      finalize(() => {
        this.isLoading = false;
        this.isLoadingMore = false;
      }),
    ).subscribe({
      next: (page) => {
        this.items = append ? [...this.items, ...page.items] : page.items;
        this.nextCursor = page.nextCursor;
        this.hasMoreItems = page.hasMore;
      },
      error: (error: unknown) => {
        this.errorText = resolveHttpErrorMessageKey(error, {
          fallback: 'error.generic',
        });
      }
    });
  }

  private getDefaultFilters(): CompaniesFilterState {
    return {
      query: '',
      status: '',
      sort: 'createdAtDesc',
    };
  }

  private normalizeSlug(value: string): string {
    return value
      .trim()
      .toLowerCase()
      .replace(/[\s_]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

}
